import {
  FilterAction,
  FilterActions,
  FilterKeys,
  VehicleFilter,
  VehicleFuelEfficiencyFilterState,
  VehicleFilterNameIdValues,
  FuelTypeFilter,
} from "@modules/vehicle/types/vehicleFilter";
import { getFuelEfficiencyFilterTranslations } from "@modules/vehicle/helpers/getVehicleFilterDescription";
import FilterDisclosure from "@modules/vehicle/components/FilterDisclosure";
import { MouseEvent, useEffect, useState } from "react";
import { Button, Listbox } from "@talladega/components";
import { useTranslation } from "next-i18next";
import { VehicleFuelType } from "@modules/vehicle/types/Vehicle";
import VehicleFuelTypeFilter from "./VehicleFuelTypeFilter";

export interface VehicleFuelEfficiencyFilterProps {
  onUpdate(filterAction: FilterAction): void;
  vehicleFilters: VehicleFilter[];
  fuelTypes: VehicleFuelType[];
}

const VehicleFuelEfficiencyFilter = ({
  onUpdate,
  vehicleFilters,
  fuelTypes,
}: VehicleFuelEfficiencyFilterProps) => {
  const { t } = useTranslation(["vehiclesFilters"]);

  const fuelTypeFilter = vehicleFilters.filter(
    (f) => f.key === FilterKeys.FuelType
  ) as FuelTypeFilter[];

  const CONSUMPTION_TYPES: VehicleFilterNameIdValues[] =
    getFuelEfficiencyFilterTranslations();

  const fuelEfficiencyFilter = vehicleFilters.find(
    (f) => f.key === FilterKeys.FuelEfficiency
  );
  const fuelEfficiencyFilterState = fuelEfficiencyFilter
    ? (fuelEfficiencyFilter.state as VehicleFuelEfficiencyFilterState)
    : null;

  const userModified =
    fuelEfficiencyFilter !== undefined || fuelTypeFilter.length > 0;

  const [consumptionType, setConsumptionType] =
    useState<VehicleFilterNameIdValues>(
      fuelEfficiencyFilterState !== null
        ? fuelEfficiencyFilterState.consumptionType
        : CONSUMPTION_TYPES[0]
    );

  const [savedConsumptionType, setSavedConsumptionType] =
    useState<VehicleFilterNameIdValues>(
      fuelEfficiencyFilterState !== null
        ? fuelEfficiencyFilterState.consumptionType
        : CONSUMPTION_TYPES[0]
    );
  const [minInputValue, setMinInputValue] = useState<number | null>(
    fuelEfficiencyFilterState !== null
      ? fuelEfficiencyFilterState.minLP100K
      : null
  );
  const [maxInputValue, setMaxInputValue] = useState<number | null>(
    fuelEfficiencyFilterState !== null
      ? fuelEfficiencyFilterState.maxLP100K
      : null
  );

  const updateSharedState = (
    consumptionTypeValue: VehicleFilterNameIdValues,
    minFuelEfficiencyValue: number | null,
    maxFuelEfficiencyValue: number | null
  ) => {
    if (minFuelEfficiencyValue == null || maxFuelEfficiencyValue == null) {
      onUpdate({
        type: FilterActions.RemoveFilter,
        payload: FilterKeys.FuelEfficiency,
      });
    } else {
      onUpdate({
        type: FilterActions.AddFuelEfficiencyFilter,
        payload: {
          consumptionType: consumptionTypeValue,
          minLP100K: minFuelEfficiencyValue,
          maxLP100K: maxFuelEfficiencyValue,
        },
      });
    }
  };

  useEffect(() => {
    if (fuelEfficiencyFilterState !== null) {
      setConsumptionType(fuelEfficiencyFilterState.consumptionType);
      setSavedConsumptionType(fuelEfficiencyFilterState.consumptionType);
      setMinInputValue(fuelEfficiencyFilterState.minLP100K);
      setMaxInputValue(fuelEfficiencyFilterState.maxLP100K);
    } else {
      setConsumptionType(getFuelEfficiencyFilterTranslations()[0]);
      setSavedConsumptionType(getFuelEfficiencyFilterTranslations()[0]);
      setMinInputValue(null);
      setMaxInputValue(null);
    }
  }, [fuelEfficiencyFilterState]);

  const onChangeConsumptionType = (item: VehicleFilterNameIdValues) => {
    if (item.id === consumptionType.id) return;
    setConsumptionType(item);
  };

  const handleConsumptionOnKeyOption = (item: VehicleFilterNameIdValues) => {
    return item.id;
  };

  const handleConsumptionOnDisplay = (item: VehicleFilterNameIdValues) => {
    return item.name;
  };

  const handleQuickSelectClick = (
    e: MouseEvent<HTMLButtonElement>,
    type: string,
    val: number
  ) => {
    if (val === maxInputValue && savedConsumptionType.id === type) {
      setMinInputValue(0);
      setMaxInputValue(null);
      setSavedConsumptionType(consumptionType);
      updateSharedState(consumptionType, null, null);
    } else {
      setMinInputValue(0);
      setMaxInputValue(val);
      setSavedConsumptionType(consumptionType);
      updateSharedState(consumptionType, 0, val);
    }
  };

  const isActive = (type: string, min: number, max: number): boolean => {
    return (
      type === savedConsumptionType.id &&
      min === minInputValue &&
      max === maxInputValue
    );
  };

  return (
    <FilterDisclosure
      active={userModified}
      childPadding="sm"
      headerText={t("vehiclesFilters:fuel_and_efficiency")}
    >
      <>
        <div className="px-4 pb-6">
          <div className="body-3 pb-3">{t("vehiclesFilters:fuel_type")}</div>
          <VehicleFuelTypeFilter
            fuelTypeFilter={fuelTypeFilter}
            fuelTypes={fuelTypes}
            onUpdate={onUpdate}
          />
        </div>
        <div className="vehicle-fuel-efficiency-filter bg-gray-25 rounded-lg p-4">
          <div className="body-3">{t("vehiclesFilters:fuel_efficiency")}</div>
          <div className="w-full py-2">
            <Listbox
              items={CONSUMPTION_TYPES}
              label={t("vehiclesFilters:consumption")}
              keyOption={(item) => handleConsumptionOnKeyOption(item)}
              displayOption={(item) =>
                handleConsumptionOnDisplay(
                  item as unknown as VehicleFilterNameIdValues
                )
              }
              displayButton={(item) =>
                handleConsumptionOnDisplay(
                  item as unknown as VehicleFilterNameIdValues
                )
              }
              valueOption={(item) => item}
              selectedItem={consumptionType}
              onChange={onChangeConsumptionType}
            />
          </div>
          <div className="my-3 basis-1/2 text-gray-500 caption-1">
            <span className="uppercase">
              {t("vehiclesFilters:quick_select")}
            </span>
            <span className="normal-case float-right">
              ({consumptionType.label})
            </span>
          </div>
          {consumptionType.id === "city" && (
            <div className="grid grid-cols-1 gap-2">
              <Button
                active={isActive("city", 0, 10)}
                fill="outline"
                buttonStyle="filter"
                onClick={(e: MouseEvent<HTMLButtonElement>) =>
                  handleQuickSelectClick(e, "city", 10.0)
                }
                childWidth="full"
              >
                <div className="w-full">
                  <span className="float-left">
                    {t("vehiclesFilters:efficient")}
                  </span>
                  <span className="float-right text-gray-600">
                    {t("vehiclesFilters:less_than_amount_lp100km", {
                      amount: "10",
                    })}
                  </span>
                </div>
              </Button>
              <Button
                active={isActive("city", 0, 8)}
                fill="outline"
                buttonStyle="filter"
                onClick={(e: MouseEvent<HTMLButtonElement>) =>
                  handleQuickSelectClick(e, "city", 8.0)
                }
                childWidth="full"
              >
                <div className="w-full">
                  <span className="float-left">
                    {t("vehiclesFilters:very_efficient")}
                  </span>
                  <span className="float-right text-gray-600">
                    {t("vehiclesFilters:less_than_amount_lp100km", {
                      amount: "8",
                    })}
                  </span>
                </div>
              </Button>
            </div>
          )}
          {consumptionType.id === "highway" && (
            <div className="grid grid-cols-1 gap-2">
              <Button
                active={isActive("highway", 0, 8)}
                fill="outline"
                buttonStyle="filter"
                className="focus:outline-none focus-visible:border-primary-bright"
                onClick={(e: MouseEvent<HTMLButtonElement>) =>
                  handleQuickSelectClick(e, "highway", 8.0)
                }
                childWidth="full"
              >
                <div className="w-full">
                  <span className="float-left">
                    {t("vehiclesFilters:efficient")}
                  </span>
                  <span className="float-right text-gray-600">
                    {t("vehiclesFilters:less_than_amount_lp100km", {
                      amount: "8",
                    })}
                  </span>
                </div>
              </Button>
              <Button
                active={isActive("highway", 0, 6)}
                fill="outline"
                buttonStyle="filter"
                className="focus:outline-none focus-visible:border-primary-bright"
                onClick={(e: MouseEvent<HTMLButtonElement>) =>
                  handleQuickSelectClick(e, "highway", 6.0)
                }
                childWidth="full"
              >
                <div className="w-full">
                  <span className="float-left">
                    {t("vehiclesFilters:very_efficient")}
                  </span>
                  <span className="float-right text-gray-600">
                    {t("vehiclesFilters:less_than_amount_lp100km", {
                      amount: "6",
                    })}
                  </span>
                </div>
              </Button>
            </div>
          )}
        </div>
      </>
    </FilterDisclosure>
  );
};
export default VehicleFuelEfficiencyFilter;
